import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const LocationsPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update document head using React 19 native features
  useEffect(() => {
    document.title = 'Home Services by Location | Taskbudy UK';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Find trusted local home services across the UK. Handyman, plumbing, electrical, cleaning and more in London, Manchester, Birmingham, and 7 other major cities.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'home services UK, local tradespeople, handyman services, plumbing services, electrical services');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://taskbudy.co.uk/locations');

    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.setAttribute('data-locations-schema', 'true');
    breadcrumbSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://taskbudy.co.uk"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Locations",
          "item": "https://taskbudy.co.uk/locations"
        }
      ]
    });
    document.head.appendChild(breadcrumbSchema);

    return () => {
      const schemasToRemove = document.querySelectorAll('script[data-locations-schema]');
      schemasToRemove.forEach(script => script.remove());
    };
  }, []);

  const locations = [
    {
      city: 'London',
      region: 'Greater London',
      description: 'Comprehensive trade services across all London boroughs',
      icon: 'ri-building-line',
      path: '/location-london'
    },
    {
      city: 'Manchester',
      region: 'Greater Manchester',
      description: 'Expert tradespeople serving the Manchester metropolitan area',
      icon: 'ri-community-line',
      path: '/location-manchester'
    },
    {
      city: 'Birmingham',
      region: 'West Midlands',
      description: 'Reliable trade services throughout Birmingham and surrounds',
      icon: 'ri-building-2-line',
      path: '/location-birmingham'
    },
    {
      city: 'Cardiff',
      region: 'South Wales',
      description: 'Quality tradespeople covering Cardiff and Vale of Glamorgan',
      icon: 'ri-home-4-line',
      path: '/location-cardiff'
    },
    {
      city: 'Edinburgh',
      region: 'Scotland',
      description: 'Professional trade services across Edinburgh and Lothians',
      icon: 'ri-ancient-gate-line',
      path: '/location-edinburgh'
    },
    {
      city: 'Bristol',
      region: 'South West England',
      description: 'Trusted tradespeople serving Bristol and surrounding areas',
      icon: 'ri-home-5-line',
      path: '/location-bristol'
    },
    {
      city: 'Glasgow',
      region: 'Scotland',
      description: 'Expert trade services throughout Greater Glasgow',
      icon: 'ri-building-3-line',
      path: '/location-glasgow'
    },
    {
      city: 'Liverpool',
      region: 'Merseyside',
      description: 'Professional tradespeople covering Liverpool and Merseyside',
      icon: 'ri-home-6-line',
      path: '/location-liverpool'
    },
    {
      city: 'Leeds',
      region: 'West Yorkshire',
      description: 'Quality trade services across Leeds and West Yorkshire',
      icon: 'ri-building-4-line',
      path: '/location-leeds'
    },
    {
      city: 'Newcastle',
      region: 'Tyne and Wear',
      description: 'Reliable tradespeople serving Newcastle and the North East',
      icon: 'ri-home-7-line',
      path: '/location-newcastle'
    },
    {
      city: 'Newport',
      region: 'South Wales',
      description: 'Professional trade services throughout Newport and Gwent',
      icon: 'ri-home-8-line',
      path: '/location-newport'
    },
    {
      city: 'Swansea',
      region: 'South Wales',
      description: 'Expert tradespeople covering Swansea and West Wales',
      icon: 'ri-home-smile-line',
      path: '/location-swansea'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/e0dadf219d3b341a9184e92001cfe9b6.png" 
                alt="Taskbudy" 
                className="h-8 lg:h-12 w-auto"
              />
            </Link>

            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Home</Link>
              <Link to="/services" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Services</Link>
              <Link to="/locations" className="text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Locations</Link>
              <Link to="/pricing" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Pricing</Link>
              <Link to="/about" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/auth/signin" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Sign In</Link>
              <Link to="/auth/signup" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">Sign Up</Link>
              <Link to="/provider-signup" className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Become a Provider
              </Link>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#0F2A44] w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="fixed top-[80px] left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200 animate-slideDown">
              <div className="flex flex-col py-2 max-h-[calc(100vh-80px)] overflow-y-auto">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Home</Link>
                <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Services</Link>
                <Link to="/locations" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-[#fe7a14] bg-orange-50 font-medium transition-all duration-300 cursor-pointer">Locations</Link>
                <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Pricing</Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">About</Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Contact</Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link to="/auth/signin" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Sign In</Link>
                <Link to="/auth/signup" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1">Sign Up</Link>
                <Link to="/provider-signup" onClick={() => setMobileMenuOpen(false)} className="mx-4 my-2 px-4 py-3 bg-gradient-to-r from-[#fe7a14] to-[#ff8c3a] text-white font-bold rounded-lg text-center hover:from-[#e56a0f] hover:to-[#fe7a14] transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-md">
                  Become a Provider
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background Design */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30"></div>
        
        {/* Large Colorful Gradient Blobs */}
        <div className="absolute -top-32 -left-32 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-to-br from-orange-400 to-amber-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/3 -right-48 w-[350px] md:w-[700px] h-[350px] md:h-[700px] bg-gradient-to-bl from-teal-400 to-cyan-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-32 left-1/4 w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-gradient-to-tr from-orange-300 to-yellow-200 rounded-full opacity-25 blur-3xl"></div>
        
        {/* Decorative Mesh Grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 117, 31, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 117, 31, 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
        
        {/* Floating Geometric Shapes */}
        <div className="absolute top-24 left-16 w-32 h-32 border-[5px] border-orange-300/50 rounded-3xl rotate-12 hidden lg:block"></div>
        <div className="absolute top-40 right-24 w-24 h-24 border-[5px] border-teal-300/50 rounded-full hidden lg:block"></div>
        <div className="absolute bottom-20 left-32 w-20 h-20 bg-gradient-to-br from-orange-200 to-orange-100 rounded-full opacity-60 hidden lg:block"></div>
        <div className="absolute bottom-32 right-16 w-28 h-28 border-[5px] border-amber-300/40 rounded-3xl -rotate-12 hidden lg:block"></div>
        
        {/* Accent Circles */}
        <div className="absolute top-32 left-1/3 w-6 h-6 bg-orange-400 rounded-full opacity-40 hidden lg:block"></div>
        <div className="absolute top-56 right-1/4 w-5 h-5 bg-teal-400 rounded-full opacity-50 hidden lg:block"></div>
        <div className="absolute bottom-40 left-1/5 w-8 h-8 bg-amber-400 rounded-full opacity-40 hidden lg:block"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 text-orange-600 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <i className="ri-map-pin-fill text-xs md:text-sm"></i>
            <span>12 Cities Across the UK</span>
          </div>
          
          <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold text-[#22304e] mb-3 md:mb-6 leading-tight">
            Professional Home Services
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500 mt-1 md:mt-2">
              Across the UK
            </span>
          </h1>
          
          <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6 md:mb-10 px-2">
            Find verified tradespeople in your area. Same-day service available with transparent pricing and quality guaranteed.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-10">
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="w-7 h-7 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-fill text-white text-xs md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs md:text-base">Verified Pros</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="w-7 h-7 md:w-10 md:h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-time-fill text-white text-xs md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs md:text-base">Same Day</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="w-7 h-7 md:w-10 md:h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg md:rounded-xl flex items-center justify-center">
                <i className="ri-price-tag-3-fill text-white text-xs md:text-lg"></i>
              </div>
              <span className="text-[#22304e] font-medium text-xs md:text-base">Fixed Pricing</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cities Grid Section */}
      <section className="py-10 md:py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-orange-50/20"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96">
          <div className="absolute inset-0 bg-gradient-to-bl from-orange-200/30 via-orange-100/20 to-transparent rounded-bl-full"></div>
        </div>
        <div className="absolute bottom-0 left-0 w-48 md:w-96 h-48 md:h-96">
          <div className="absolute inset-0 bg-gradient-to-tr from-teal-200/30 via-teal-100/20 to-transparent rounded-tr-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-14">
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 text-orange-600 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-6">
              <i className="ri-compass-3-fill text-xs md:text-sm"></i>
              <span>Service Areas</span>
            </div>
            <h2 className="text-xl md:text-4xl lg:text-5xl font-bold text-[#22304e] mb-2 md:mb-4">
              Choose Your Location
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
              Select your city to view local services and pricing
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {locations.map((location) => (
              <Link
                key={location.city}
                to={location.path}
                className="group relative bg-white rounded-lg md:rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                {/* Accent bar - always visible */}
                <div className="absolute top-0 left-0 right-0 h-0.5 md:h-1 bg-gradient-to-r from-orange-500 to-orange-600"></div>
                
                {/* Orange glow on hover */}
                <div className="absolute inset-0 border-2 border-orange-500/0 group-hover:border-orange-500/50 rounded-lg md:rounded-xl transition-all duration-300"></div>
                
                <div className="p-3 md:p-6">
                  {/* Icon */}
                  <div className="w-8 h-8 md:w-11 md:h-11 flex items-center justify-center mb-2 md:mb-4">
                    <i className={`${location.icon} text-xl md:text-3xl text-orange-500`}></i>
                  </div>
                  
                  {/* City name */}
                  <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-0.5 md:mb-1 group-hover:text-orange-600 transition-colors">
                    {location.city}
                  </h3>
                  
                  {/* Region */}
                  <p className="text-xs md:text-sm text-gray-500 mb-1 md:mb-3">
                    {location.region}
                  </p>
                  
                  {/* Description - hidden on mobile */}
                  <p className="hidden md:block text-sm text-gray-600 leading-relaxed">
                    {location.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why TaskBudy Section */}
      <section className="py-10 md:py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#22304e] via-[#2a3a5e] to-[#1a2540]"></div>
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-gradient-to-br from-orange-500 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-40 md:w-80 h-40 md:h-80 bg-gradient-to-tr from-teal-500 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        {/* Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-14">
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-6">
              <i className="ri-sparkling-fill text-orange-400 text-xs md:text-sm"></i>
              <span>Why Choose TaskBudy</span>
            </div>
            <h2 className="text-xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4">
              Home Services, Simplified
            </h2>
            <p className="text-sm md:text-lg text-gray-300 max-w-2xl mx-auto">
              One platform for all your home service needs
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {[
              { icon: 'ri-shield-user-fill', title: 'Verified Pros', desc: 'ID-verified and background-checked', color: 'from-orange-500 to-amber-500' },
              { icon: 'ri-price-tag-3-fill', title: 'Clear Pricing', desc: 'No quotes, see prices upfront', color: 'from-teal-500 to-emerald-500' },
              { icon: 'ri-flashlight-fill', title: 'Fast Service', desc: 'Same-day availability', color: 'from-rose-500 to-pink-500' },
              { icon: 'ri-customer-service-2-fill', title: '24/7 Support', desc: 'Always here to help', color: 'from-sky-500 to-cyan-500' },
            ].map((item, index) => (
              <div key={index} className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 md:p-6 hover:bg-white/10 transition-all duration-300">
                <div className={`w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br ${item.color} rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <i className={`${item.icon} text-lg md:text-2xl text-white`}></i>
                </div>
                <h3 className="text-xs md:text-lg font-bold text-white mb-1 md:mb-2">{item.title}</h3>
                <p className="text-gray-400 text-[10px] md:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="mt-8 md:mt-14 grid grid-cols-4 gap-2 md:gap-6">
            {[
              { value: '12+', label: 'Cities', icon: 'ri-map-pin-fill' },
              { value: '6', label: 'Services', icon: 'ri-tools-fill' },
              { value: '100%', label: 'Verified', icon: 'ri-shield-check-fill' },
              { value: '24/7', label: 'Booking', icon: 'ri-time-fill' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-white/10 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-1.5 md:mb-3">
                  <i className={`${stat.icon} text-sm md:text-xl text-orange-400`}></i>
                </div>
                <div className="text-base md:text-3xl font-bold text-white mb-0.5 md:mb-1">{stat.value}</div>
                <div className="text-[10px] md:text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-teal-50"></div>
        
        {/* Decorative Blobs */}
        <div className="absolute -top-20 -right-20 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-to-br from-orange-300 to-amber-200 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-gradient-to-tr from-teal-300 to-cyan-200 rounded-full opacity-30 blur-3xl"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-200/60 text-orange-600 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-6">
            <i className="ri-question-fill text-xs md:text-sm"></i>
            <span>Don&apos;t See Your Location?</span>
          </div>
          
          <h2 className="text-xl md:text-4xl lg:text-5xl font-bold text-[#22304e] mb-3 md:mb-6">
            We&apos;re Expanding Across the UK
          </h2>
          
          <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-10 max-w-2xl mx-auto">
            Contact us to check availability in your area or to be notified when we launch.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#ff751f] to-[#ff9147] text-white px-6 py-3 md:px-8 md:py-4 rounded-lg md:rounded-xl font-semibold text-sm md:text-base hover:from-[#e56a0f] hover:to-[#ff751f] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line text-base md:text-lg"></i>
            </Link>
            <Link
              to="/service-request"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#22304e] border-2 border-gray-200 px-6 py-3 md:px-8 md:py-4 rounded-lg md:rounded-xl font-semibold text-sm md:text-base hover:border-[#ff751f] hover:text-[#ff751f] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Book a Service
              <i className="ri-calendar-line text-base md:text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-14">
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-200/60 text-teal-600 px-3 py-1.5 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-6">
              <i className="ri-question-answer-fill text-xs md:text-sm"></i>
              <span>FAQ</span>
            </div>
            <h2 className="text-xl md:text-4xl lg:text-5xl font-bold text-[#22304e] mb-2 md:mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-3 md:gap-5 max-w-6xl mx-auto">
            {[
              { question: 'How does Taskbudy work?', answer: "Simply describe the problem, choose whether you need help ASAP or at a later date, and we'll match you with a trusted local professional. You'll see the price before booking and can track the job from acceptance to arrival.", color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
              { question: 'What services can I book?', answer: 'With Taskbudy, you can book handyman services, plumbing, electrical work, cleaning, rubbish & waste removal, and transportation & deliveries. Availability may vary by area and time.', color: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
              { question: 'Can I get same-day or emergency service?', answer: 'Yes. Same-day and emergency bookings are available depending on local availability. Many customers are matched with a professional within the hour.', color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
              { question: 'How much will it cost?', answer: "Pricing depends on the service, location, and job type. You'll always see clear pricing before confirming your booking — no hidden fees or surprise charges.", color: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
              { question: 'Do I need to get quotes?', answer: 'No. Taskbudy removes the hassle of quotes and bidding. Job details and pricing are shown upfront so you can book quickly with confidence.', color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
              { question: 'Are Taskbudy professionals verified?', answer: 'Yes. All professionals are ID-verified, background-checked, and insured. Providers are also rated after every job to maintain quality standards.', color: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
              { question: 'Can I choose a later date instead of ASAP?', answer: 'Yes. You can book immediately or schedule a service for a date and time that suits you, subject to availability.', color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
              { question: 'What areas do you cover?', answer: 'Taskbudy operates across multiple UK cities. Enter your postcode during booking to confirm coverage and see services available in your area.', color: 'bg-gradient-to-br from-teal-500 to-emerald-500' },
              { question: "What if I'm not satisfied with the service?", answer: "We stand behind our work. If you're not satisfied, our support team will help resolve the issue in line with our quality guarantee.", color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
              { question: 'Is support available if I need help?', answer: 'Yes. Our customer support team is available to assist you with bookings, questions, or any issues before, during, or after your service.', color: 'bg-gradient-to-br from-teal-500 to-emerald-500' }
            ].map((faq, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100 hover:border-transparent hover:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300"
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className={`w-8 h-8 md:w-10 md:h-10 ${faq.color} rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <i className="ri-question-line text-white text-sm md:text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-base font-bold text-[#22304e] mb-1 md:mb-2 group-hover:text-[#ff751f] transition-colors duration-300">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA after FAQ */}
          <div className="text-center mt-8 md:mt-14">
            <p className="text-sm md:text-lg text-gray-600 mb-4 md:mb-6">
              Ready to book a service?
            </p>
            <Link
              to="/service-request"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#ff751f] to-[#ff9147] text-white px-6 py-3 md:px-8 md:py-4 rounded-lg md:rounded-xl font-semibold text-sm md:text-base hover:from-[#e56a0f] hover:to-[#ff751f] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Started Now
              <i className="ri-arrow-right-line text-base md:text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationsPage;
